import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { useAxiosAuth } from '../hooks/useAxiosAuth';


function formatCreatedAt(value) {
  if (!value) {
    return 'Just now';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Unknown';
  }
  return date.toLocaleString();
}


export default function TeacherApiKeys() {
  const axiosAuth = useAxiosAuth();
  const [label, setLabel] = useState('');
  const [keys, setKeys] = useState([]);
  const [revealedKey, setRevealedKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const sortedKeys = useMemo(
    () => [...keys].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)),
    [keys]
  );

  const loadKeys = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axiosAuth.get('/teacher/api-keys');
      setKeys(response.data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load teacher API keys.');
    } finally {
      setLoading(false);
    }
  }, [axiosAuth]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleCreate = async () => {
    const trimmed = label.trim();
    if (!trimmed) {
      setError('Key label is required.');
      return;
    }

    setCreating(true);
    setError('');
    setSuccess('');
    try {
      const response = await axiosAuth.post('/teacher/api-keys', { label: trimmed });
      const created = response.data;
      setRevealedKey(created.api_key);
      setLabel('');
      setSuccess('API key generated. Store it now; it will not be shown again.');
      await loadKeys();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to create API key.');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!revealedKey || !navigator?.clipboard?.writeText) {
      return;
    }
    await navigator.clipboard.writeText(revealedKey);
    setSuccess('API key copied to clipboard.');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Teacher API Keys
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Generate a teacher billing key for course-generator integrations. The raw key is shown only once.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Key Label"
            placeholder="e.g. QuantumLearn production"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            disabled={creating}
          />
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating}
            sx={{ minWidth: { md: 220 } }}
          >
            Generate API Key
          </Button>
        </Stack>

        {revealedKey && (
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3, bgcolor: 'warning.50' }}>
            <Typography variant="subtitle1" gutterBottom>
              Store this key now
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The full key is not retrievable after this response. Save it in your deployment secret store.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                value={revealedKey}
                InputProps={{ readOnly: true }}
              />
              <IconButton aria-label="Copy API key" onClick={handleCopy}>
                <ContentCopyIcon />
              </IconButton>
            </Box>
          </Paper>
        )}

        <Divider sx={{ mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Existing Keys
        </Typography>

        {loading ? (
          <Typography color="text.secondary">Loading keys…</Typography>
        ) : sortedKeys.length === 0 ? (
          <Typography color="text.secondary">No teacher API keys created yet.</Typography>
        ) : (
          <List disablePadding>
            {sortedKeys.map((item, index) => (
              <React.Fragment key={item.key_id}>
                {index > 0 && <Divider component="li" />}
                <ListItem
                  secondaryAction={
                    <Chip
                      size="small"
                      color={item.active ? 'success' : 'default'}
                      label={item.active ? 'Active' : 'Inactive'}
                    />
                  }
                >
                  <ListItemText
                    primary={item.label || 'Unnamed key'}
                    secondary={`••••${item.hint || '----'} · ${formatCreatedAt(item.created_at)}`}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Container>
  );
}
